import { apiBase } from 'api/api';
import { ApiResponse } from 'api/interface/api';
import { getCETTimestamp } from 'util/dateTimeConverter';
import {
    DeleteCameraSettingsByIdRequest,
    DeleteCameraSettingsByIdResponse,
    GetCameraSettingsByIdRequest,
    GetCameraSettingsByIdResponse,
    GetListOfCameraSettingsRequest,
    GetListOfCameraSettingsResponse,
    UpdateCameraSettingsByIdRequest,
    UpdateCameraSettingsByIdResponse,
    UpdateCameraSettingForStreamRequest,
    UpdateCameraSettingForStreamResponse,
    CameraAddressTimeRule,
    CameraSettings,
    LedInfo,
    GetLedInfoResponse,
    Address,
    CameraSettingsByAddress,
    GetCameraSettingsByAddressIdResponse,
    GetCameraSettingsByAddressIdRequest,
    UpdateSharedPayload,
    UpdateSharedResponse,
    DeleteByAddressResponse,
    CreateCameraSettings,
} from 'api/interface/cameraSettings';

const getAllCameraSettings = async (
    req: GetListOfCameraSettingsRequest
): Promise<GetListOfCameraSettingsResponse> => {
    const res = await apiBase.get(
        `/v3/camerasettings?limit=${req.limit}&offset=${req.offset}&sort_order=${req.sortorder}&sort_col=${req.sortcol}&search_term=${req.search_term}&parkinglot_ids=${req.parkinglot_ids}`
    );

    if (res.status !== 200) {
        return {
            error: 'bad request',
            statusCode: res.status,
            payload: { data: [], total: 0 },
        };
    }

    const transformedData: CameraSettings[] = res.data.data.map(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (item: { camera_settings: any; address: any }) => {
            const cs = item.camera_settings;
            const addr = item.address;

            return {
                ...cs,
                address_name: addr?.address_name ?? '',
                address_id: cs.address_id ?? 0,
                buffer_time: cs.parking_buffer_time ?? 0,
            };
        }
    );

    return {
        error: null,
        statusCode: res.status,
        payload: { data: transformedData, total: res.data.total_count },
    };
};

const getCameraSettingById = async (
    req: GetCameraSettingsByIdRequest
): Promise<GetCameraSettingsByIdResponse> => {
    const res = await apiBase.get(`/v3/camerasettings/${req.id}`);

    if (res.status !== 200) {
        return {
            payload: {
                id: 0,
                name: '',
                address_id: 0,
                address_name: '',
                active: false,
                status: 'active',
                url: '',
                username: '',
                password: '',
                gate_control: false,
                last_sync_date: null,
                type: '',
                created_at: '',
                updated_at: '',
                dedicated_direction: null,
                reverse_direction: false,
                multiple_address: null,
                entrance_angle: 0,
                entrance_tolerance: 0,
                exit_angle: 0,
                exit_tolerance: 0,
                gateway_address_id: null,
                detection_zone: null,
                make_camerafine: true,
                make_delayed_fine: false,
                easy_park_enabled: false,
                parking_time_limit: 0,
                permit_check_leaving: false,
                min_park_time: 0,
                parking_buffer_time: 0,
                parking_buffer_percent: 0,
                buffer_time: 0,
                is_paused: false,
                pause_start_time: '',
                pause_end_time: '',
                external_camera_id: null,
                ParentAddressIds: '',
                skip_fine_for_valid_reception_permit: false,
                oparko_go_minimum_park_time: 0,
            },
            error: 'bad request',
            statusCode: res.status,
        };
    }
    const cameraSettings = res.data.camera_settings;
    const address = res.data.address;

    const merged: CameraSettings = {
        ...cameraSettings,
        address_name: address?.address_name ?? '',
        buffer_time: cameraSettings.parking_buffer_time ?? 0,
        ParentAddressIds: res.data.parent_ids,
        created_at: getCETTimestamp(cameraSettings.created_at),
        updated_at: getCETTimestamp(cameraSettings.updated_at),
        last_sync_date: cameraSettings.last_sync_date
            ? getCETTimestamp(cameraSettings.last_sync_date)
            : null,
        pause_start_time: cameraSettings.pause_start_time_utc
            ? getCETTimestamp(cameraSettings.pause_start_time_utc)
            : null,
        pause_end_time: cameraSettings.pause_end_time_utc
            ? getCETTimestamp(cameraSettings.pause_end_time_utc)
            : null,
    };
    return {
        error: null,
        statusCode: res.status,
        payload: merged,
    };
};

const updateCameraSetting = async (
    req: UpdateCameraSettingsByIdRequest
): Promise<UpdateCameraSettingsByIdResponse> => {
    const res = await apiBase.put(`/v3/camerasettings/${req.id}`, {
        ...req.payload,
    });

    if (res.status !== 200) {
        return {
            payload: {},
            error: 'bad request',
            statusCode: res.status,
        };
    }
    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    } as UpdateCameraSettingsByIdResponse;
};

const updateSharedCameraSettings = async (
    body: UpdateSharedPayload
): Promise<ApiResponse & { message?: string }> => {
    try {
        const res = await apiBase.put<UpdateSharedResponse>(
            '/v3/camerasettings/shared',
            body
        );

        if (res.status < 200 || res.status >= 300) {
            return {
                statusCode: res.status,
                // eslint-disable-next-line
                error: (res.data as any)?.error ?? res.status,
            };
        }

        return {
            statusCode: res.status,
            error: null,
            message: res.data?.message,
        };
        // eslint-disable-next-line
    } catch (err: any) {
        const status = err?.response?.status ?? 500;
        const msg = err?.response?.data?.error ?? 'bad request';
        return { statusCode: status, error: msg };
    }
};

const createCameraSetting = async (
    req: CreateCameraSettings
): Promise<UpdateCameraSettingsByIdResponse> => {
    const res = await apiBase.post(`/v3/camerasettings`, req);

    if (res.status !== 200 && res.status !== 201) {
        return { payload: {}, error: 'bad request', statusCode: res.status };
    }
    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    } as UpdateCameraSettingsByIdResponse;
};

const deleteCameraSetting = async (
    req: DeleteCameraSettingsByIdRequest
): Promise<DeleteCameraSettingsByIdResponse> => {
    const res = await apiBase.delete(`/v3/camerasettings/delete/${req.id}`);

    if (res.status !== 200) {
        return {
            payload: {},
            error: 'bad request',
            statusCode: res.status,
        };
    }
    return {
        error: null,
        statusCode: res.status,
        payload: res.data,
    } as DeleteCameraSettingsByIdResponse;
};

const deleteCameraSettingsByAddress = async (
    addressId: number
): Promise<ApiResponse & { deleted?: number }> => {
    try {
        const res = await apiBase.delete<DeleteByAddressResponse>(
            `/v3/camerasettings/addresses/${addressId}`
        );

        if (res.status < 200 || res.status >= 300) {
            return {
                statusCode: res.status,
                // eslint-disable-next-line
                error: (res.data as any)?.error ?? res.status,
            };
        }

        return {
            statusCode: res.status,
            error: null,
            deleted: res.data?.deleted ?? 0,
        };
        // eslint-disable-next-line
    } catch (err: any) {
        const status = err?.response?.status ?? 500;
        const msg = err?.response?.data?.error ?? 'bad request';
        return { statusCode: status, error: msg };
    }
};

const getCameraTypes = async (): Promise<string[]> => {
    const res = await apiBase.get(`/camera-settings/get-types`);

    if (res.status !== 200) {
        return [];
    }

    return res.data as string[];
};

const getLedInfoByParkingAddressId = async (
    addressId: number
): Promise<GetLedInfoResponse> => {
    const res = await apiBase.get(`/v3/camerasettings/led-info/${addressId}`);

    if (res.status !== 200) {
        return {
            error: 'Error while fetching led sign info',
            payload: { data: [] },
            statusCode: res.status,
        };
    }

    return { error: null, payload: { data: res.data }, statusCode: 200 };
};

const updateLedInfoByParkingAddressId = async (
    body: LedInfo
): Promise<ApiResponse> => {
    const res = await apiBase.put(
        `/v3/camerasettings/led-info/${body.id}`,
        body
    );

    if (res.status !== 200) {
        return {
            error: 'Error while updating led sign info',
            statusCode: res.status,
        };
    }

    return { error: null, statusCode: 200 };
};

const updateCameraSettingForStream = async (
    req: UpdateCameraSettingForStreamRequest
): Promise<UpdateCameraSettingForStreamResponse> => {
    const res = await apiBase.post(`/camera-settings/update-forstream`, {
        ...req.payload,
    });

    if (res.status !== 200) {
        return {
            payload: {
                error: 'error',
                details: res.data.details,
            },
        } as UpdateCameraSettingForStreamResponse;
    }

    return {
        payload: {
            error: '',
            details: '',
        },
    } as UpdateCameraSettingForStreamResponse;
};

const getCameraSettingsByAddressId = async (
    req: GetCameraSettingsByAddressIdRequest
): Promise<GetCameraSettingsByAddressIdResponse> => {
    const res = await apiBase.get<CameraSettingsByAddress>(
        `v3/camerasettings/addresses/${req.addressId}`
    );

    if (res.status !== 200) {
        return {
            payload: {
                address: null,
                camera_settings: [],
                time_rules: [],
            },
            error: 'bad request',
            statusCode: res.status,
        };
    }

    const { address, camera_settings, time_rules } = res.data as {
        address: Address | null | undefined;
        camera_settings: CameraSettings[];
        time_rules: CameraAddressTimeRule[];
    };

    const cameraSettings: CameraSettings[] = camera_settings.map(
        (cs: CameraSettings) => ({
            ...cs,
            address_name: address?.address_name ?? '',
        })
    );

    return {
        payload: {
            address: address ?? null,
            camera_settings: cameraSettings,
            time_rules: time_rules ?? [],
        },
        error: null,
        statusCode: res.status,
    };
};

export const backofficeCameraSettings = {
    getAllCameraSettings,
    getCameraSettingById,
    updateCameraSetting,
    updateSharedCameraSettings,
    createCameraSetting,
    deleteCameraSetting,
    deleteCameraSettingsByAddress,
    getCameraTypes,
    updateCameraSettingForStream,
    getLedInfoByParkingAddressId,
    updateLedInfoByParkingAddressId,
    getCameraSettingsByAddressId,
};
